import React, { useState, useEffect } from 'react';
import { Heart, MessageCircle, Share2, Clock, Edit, Trash, ExternalLink, MoreHorizontal } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { profileThumb, profileThumbFallback } from '../utils/imageHelpers';
import ContributingArtistsDisplay from './ContributingArtistsDisplay';
import RichDescription from './RichDescription';
import ShareModal from './ShareModal';
import api from '../services/api';

const PostsFeed = ({ profileUsername, refreshTrigger, limit = null, onTotalCount }) => {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [shareModal, setShareModal] = useState({ isOpen: false, post: null });
    const [editingPostId, setEditingPostId] = useState(null);
    const [editContent, setEditContent] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);

    useEffect(() => {
        fetchPosts(1);
    }, [profileUsername, refreshTrigger]);

    const fetchPosts = async (pageNum = 1) => {
        try {
            setLoading(pageNum === 1);
            setError(null);

            const pageLimit = limit ?? 20;
            const response = await api.get(`/posts/profile/${profileUsername}?page=${pageNum}&limit=${pageLimit}`);
            
            if (response.data.success) {
                const newPosts = response.data.data;
                
                if (pageNum === 1) {
                    setPosts(newPosts);
                } else {
                    setPosts(prev => [...prev, ...newPosts]);
                }
                
                setHasMore(newPosts.length === 20); // If we got 20 posts, there might be more
                setPage(pageNum);

                if (onTotalCount && response.data.pagination?.total !== undefined) {
                    onTotalCount(response.data.pagination.total);
                }
            } else {
                setError('Failed to load posts');
            }
        } catch (err) {
            console.error('Error fetching posts:', err);
            setError('Failed to load posts');
        } finally {
            setLoading(false);
        }
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
        return date.toLocaleDateString();
    };

    const handleLike = async (postId) => {
        if (!isAuthenticated) return;

        try {
            const response = await api.post(`/posts/${postId}/like`);
            
            if (response.data.success) {
                setPosts(prev => prev.map(post => 
                    post.id === postId 
                        ? { 
                            ...post, 
                            like_count: response.data.data.likeCount,
                            user_has_liked: response.data.data.liked 
                        }
                        : post
                ));
            }
        } catch (err) {
            console.error('Error liking post:', err);
        }
    };

    const handleDelete = async (postId) => {
        if (!confirm('Are you sure you want to delete this post?')) return;

        try {
            const response = await api.delete(`/posts/${postId}`);
            
            if (response.data.success) {
                setPosts(prev => prev.filter(post => post.id !== postId));
            }
        } catch (err) {
            console.error('Error deleting post:', err);
        }
    };

    const handleShare = (post) => {
        setShareModal({ isOpen: true, post });
    };

    const handleEditStart = (post) => {
        setEditingPostId(post.id);
        setEditContent(post.content);
    };

    const handleEditCancel = () => {
        setEditingPostId(null);
        setEditContent('');
    };

    const handleEditSave = async (postId) => {
        if (!editContent.trim()) return;

        try {
            setSavingEdit(true);
            const response = await api.put(`/posts/${postId}`, { content: editContent.trim() });

            if (response.data.success) {
                setPosts(prev => prev.map(post =>
                    post.id === postId ? { ...post, content: response.data.data.content } : post
                ));
                setEditingPostId(null);
                setEditContent('');
            }
        } catch (err) {
            console.error('Error updating post:', err);
        } finally {
            setSavingEdit(false);
        }
    };

    const loadMore = () => {
        if (!loading && hasMore) {
            fetchPosts(page + 1);
        }
    };

    if (loading && posts.length === 0) {
        return (
            <div className="space-y-6">
                {[...Array(3)].map((_, i) => (
                    <div key={i} className="glass-card p-6 border border-gray-700/50 animate-pulse">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 bg-gray-700 rounded-full"></div>
                            <div className="flex-1">
                                <div className="h-4 bg-gray-700 rounded w-32 mb-2"></div>
                                <div className="h-3 bg-gray-700 rounded w-24 mb-4"></div>
                                <div className="space-y-2">
                                    <div className="h-4 bg-gray-700 rounded w-full"></div>
                                    <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className="glass-card p-8 border border-red-500/30 bg-red-500/5 text-center">
                <div className="text-red-400 text-lg mb-2">⚠️ Error Loading Posts</div>
                <p className="text-gray-400 mb-4">{error}</p>
                <button
                    onClick={() => fetchPosts(1)}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                    Try Again
                </button>
            </div>
        );
    }

    if (posts.length === 0) {
        // Only the profile owner sees the empty state (with a nudge to post) —
        // visitors just see nothing instead of a "hasn't posted anything yet" card.
        if (profileUsername !== user?.profile?.username) {
            return null;
        }
        return (
            <div className="glass-card p-12 text-center border border-gray-700/50">
                <div className="text-6xl mb-4">📝</div>
                <h3 className="text-xl font-bold text-white mb-2">No Posts Yet</h3>
                <p className="text-gray-400">
                    Share your first post with the community!
                </p>
            </div>
        );
    }

    const visiblePosts = limit ? posts.slice(0, limit) : posts;

    return (
        <div className="space-y-6">
            {visiblePosts.map((post) => (
                <div key={post.id} className="glass-card p-6 border border-gray-700/50 hover:border-gray-600/50 transition-colors">
                    {/* Post Header */}
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center flex-shrink-0">
                                {post.author_avatar ? (
                                    <img
                                        src={profileThumb(post.author_avatar)}
                                        alt={post.author_display_name}
                                        width="40"
                                        height="40"
                                        className="w-10 h-10 rounded-full object-cover"
                                        style={{ background: 'transparent' }}
                                        onError={profileThumbFallback(post.author_avatar)}
                                    />
                                ) : (
                                    <span className="text-white font-semibold">
                                        {post.author_display_name?.charAt(0) || post.author_username?.charAt(0) || 'U'}
                                    </span>
                                )}
                            </div>
                            <div>
                                <p className="font-medium text-white">
                                    {post.author_display_name || post.author_username}
                                </p>
                                <p className="text-sm text-gray-400">
                                    @{post.author_username} • {formatTimeAgo(post.created_at)}
                                </p>
                            </div>
                        </div>

                        {/* Post Actions Menu */}
                        {isAuthenticated && user?.profile?.username === post.author_username && (
                            <div className="relative group">
                                <button aria-label="Post options" className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-700/50 transition-colors">
                                    <MoreHorizontal className="w-4 h-4" />
                                </button>
                                <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 min-w-[120px]">
                                    <button
                                        onClick={() => handleEditStart(post)}
                                        className="flex items-center gap-2 w-full px-3 py-2 text-gray-300 hover:bg-gray-700/50 transition-colors text-sm"
                                    >
                                        <Edit className="w-3 h-3" />
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDelete(post.id)}
                                        className="flex items-center gap-2 w-full px-3 py-2 text-red-400 hover:bg-gray-700/50 transition-colors text-sm"
                                    >
                                        <Trash className="w-3 h-3" />
                                        Delete
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Post Content */}
                    <div className="mb-4">
                        {editingPostId === post.id ? (
                            <div>
                                <textarea
                                    value={editContent}
                                    onChange={(e) => setEditContent(e.target.value)}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-400 resize-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                                    rows="4"
                                    autoFocus
                                />
                                <div className="flex justify-end gap-2 mt-2">
                                    <button
                                        onClick={handleEditCancel}
                                        disabled={savingEdit}
                                        className="px-4 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => handleEditSave(post.id)}
                                        disabled={savingEdit || !editContent.trim()}
                                        className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                                    >
                                        {savingEdit ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <RichDescription
                                description={post.content}
                                maxLength={400}
                                showLabel={false}
                                textClassName="text-gray-300"
                            />
                        )}
                    </div>

                    {/* Post Attachments - Show first 4 images only */}
                    {post.attachments && post.attachments.length > 0 && (
                        <div className="mb-4">
                            <div className={`grid gap-3 ${
                                Math.min(post.attachments.length, 4) === 1 ? 'grid-cols-1' :
                                Math.min(post.attachments.length, 4) === 2 ? 'grid-cols-2' :
                                Math.min(post.attachments.length, 4) === 3 ? 'grid-cols-3' :
                                'grid-cols-2'
                            }`}>
                                {post.attachments.slice(0, 4).map((attachment, index) => (
                                    <div 
                                        key={attachment.id} 
                                        className={`relative ${
                                            Math.min(post.attachments.length, 4) === 4 && index === 3 && post.attachments.length > 4 ? 'relative' : ''
                                        }`}
                                    >
                                        <Link to={`/view/image/${attachment.uploader_username}/${attachment.stored_filename || attachment.original_filename || attachment.upload_id}`} className="block">
                                            <img
                                                src={attachment.optimized_url || attachment.file_url}
                                                alt={attachment.alt_text || attachment.title || 'Image'}
                                                className="w-full aspect-square object-cover rounded-lg border border-gray-600 hover:border-gray-500 transition-colors"
                                                loading="lazy"
                                            />
                                        </Link>
                                        
                                        {/* "View More" overlay on 4th image if there are more images */}
                                        {index === 3 && post.attachments.length > 4 && (
                                            <Link
                                                to={`/view/post/${profileUsername}/${post.id}`}
                                                className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                                            >
                                                <div className="text-center">
                                                    <ExternalLink className="w-6 h-6 mx-auto mb-1" />
                                                    <div className="text-sm font-medium">
                                                        +{post.attachments.length - 4} more
                                                    </div>
                                                    <div className="text-xs text-gray-300">
                                                        View Post
                                                    </div>
                                                </div>
                                            </Link>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* "View Full Post" button if there are more than 4 images */}
                            {post.attachments.length > 4 && (
                                <div className="mt-3 text-center">
                                    <Link
                                        to={`/view/post/${profileUsername}/${post.id}`}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm text-blue-400 hover:text-blue-300 bg-blue-400/10 hover:bg-blue-400/20 rounded-lg transition-colors"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                        View all {post.attachments.length} images
                                    </Link>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Contributing Artists */}
                    <div className="mb-4">
                        <ContributingArtistsDisplay
                            mediaType="post"
                            mediaId={post.id}
                            layout="compact"
                            maxVisible={3}
                            showTitle={false}
                        />
                    </div>

                    {/* Post Interactions */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-700/50">
                        <div className="flex items-center gap-6">
                            <button
                                onClick={() => handleLike(post.id)}
                                disabled={!isAuthenticated}
                                className={`flex items-center gap-2 text-sm transition-colors ${
                                    post.user_has_liked 
                                        ? 'text-red-400 hover:text-red-300' 
                                        : 'text-gray-400 hover:text-red-400'
                                } ${!isAuthenticated ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                            >
                                <Heart className={`w-4 h-4 ${post.user_has_liked ? 'fill-current' : ''}`} />
                                <span>{post.like_count || 0}</span>
                            </button>

                            <button 
                                disabled={!isAuthenticated}
                                className="flex items-center gap-2 text-sm text-gray-400 hover:text-blue-400 transition-colors cursor-pointer"
                            >
                                <MessageCircle className="w-4 h-4" />
                                <span>{post.comment_count || 0}</span>
                            </button>

                            <button 
                                onClick={() => handleShare(post)}
                                className="flex items-center gap-2 text-sm text-gray-400 hover:text-blue-400 transition-colors cursor-pointer"
                            >
                                <Share2 className="w-4 h-4" />
                                <span>Share</span>
                            </button>

                            <Link
                                to={`/view/post/${profileUsername}/${post.id}`}
                                className="flex items-center gap-2 text-sm text-gray-400 hover:text-blue-400 transition-colors"
                            >
                                <ExternalLink className="w-4 h-4" />
                                <span>View Post</span>
                            </Link>
                        </div>

                        <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Clock className="w-3 h-3" />
                            {formatTimeAgo(post.created_at)}
                        </div>
                    </div>
                </div>
            ))}

            {/* Load More Button */}
            {!limit && hasMore && (
                <div className="text-center pt-6">
                    <button
                        onClick={loadMore}
                        disabled={loading}
                        className="px-6 py-3 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:opacity-50 text-white rounded-lg transition-colors font-medium"
                    >
                        {loading ? 'Loading...' : 'Load More Posts'}
                    </button>
                </div>
            )}

            {!limit && !hasMore && posts.length > 0 && (
                <div className="text-center pt-6">
                    <p className="text-gray-500 text-sm">
                        You've reached the end of the posts!
                    </p>
                </div>
            )}

            {/* Share Modal */}
            <ShareModal
                isOpen={shareModal.isOpen}
                onClose={() => setShareModal({ isOpen: false, post: null })}
                mediaTitle={shareModal.post?.content?.substring(0, 50) + '...' || 'Post'}
                mediaType="post"
                username={shareModal.post?.author_username}
                mediaId={shareModal.post?.id}
                description={shareModal.post?.content}
                artistName={shareModal.post?.author_display_name || shareModal.post?.author_username}
                imageUrl={shareModal.post?.attachments?.[0]?.optimized_url || shareModal.post?.attachments?.[0]?.file_url}
            />
        </div>
    );
};

export default PostsFeed;